// src/components/mpi/MpiTrendModal.js
// Modal-Overlay mit MPI-Mehrperioden-Verlauf (Recharts). Muster: D3RiskDrillDown.
import { useEffect, useState, useRef, useCallback } from 'react';
import {
  ResponsiveContainer, LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, LabelList,
} from 'recharts';
import { mentoringApi as axios } from '../../axiosInstance';

const LINE_COLOR = '#2E75B6';
const AXIS_COLOR = 'rgba(255,255,255,0.45)';
const GRID_COLOR = 'rgba(255,255,255,0.08)';
const LABEL_COLOR = 'rgba(255,255,255,0.7)';

// 'YYYY-Qn' -> 'Qn-YY' (generisch abgeleitet, nicht hardcodiert)
function periodLabel(p) {
  if (typeof p !== 'string') return String(p ?? '');
  const m = p.match(/^(\d{4})-Q([1-4])$/i);
  if (m) return `Q${m[2]}-${m[1].slice(2)}`;
  return p;
}

// 75.4 -> '75,4' (Komma statt Punkt); null/NaN -> '' (nicht beschriften)
function fmtComma(v) {
  if (v == null || Number.isNaN(Number(v))) return '';
  return Number(v).toFixed(1).replace('.', ',');
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const v = payload[0]?.value;
  return (
    <div style={{
      background: 'rgba(8,11,18,0.95)', border: '1px solid rgba(255,255,255,0.12)',
      borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'rgba(255,255,255,0.85)',
    }}>
      <div style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 700, color: LINE_COLOR }}>MPI {fmtComma(v) || '—'}</div>
    </div>
  );
}

export default function MpiTrendModal({ tenant, period: initialPeriod, onClose, label = 'MPI-Verlauf' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const boxRef = useRef(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    setError('');
    const tq = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
    axios.get(`/mpi/scores/trend${tq}`, { withCredentials: true })
      .then(res => setData(Array.isArray(res.data) ? res.data : []))
      .catch(() => setError('Verlaufsdaten konnten nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, [tenant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleOverlayClick(e) {
    if (boxRef.current && !boxRef.current.contains(e.target)) onClose();
  }

  // ── Datenmapping fuer Recharts ──
  const chartData = (data ?? []).map(d => ({
    period: d.period,
    label: periodLabel(d.period),
    mpi_total: d.mpi_total == null ? null : Number(d.mpi_total),
  }));
  const validCount = chartData.filter(d => d.mpi_total != null).length;

  return (
    <div
      onClick={handleOverlayClick}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.72)',
        backdropFilter: 'blur(8px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        ref={boxRef}
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 720,
          maxHeight: '88vh',
          background: 'linear-gradient(160deg, #111827 0%, #080b12 100%)',
          border: `1px solid ${LINE_COLOR}33`,
          borderRadius: 20,
          display: 'flex', flexDirection: 'column',
          boxShadow: '0 32px 80px rgba(0,0,0,0.8), 0 0 0 1px rgba(255,255,255,0.04)',
          overflow: 'hidden',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: '20px 24px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 8, height: 8, borderRadius: '50%',
              background: LINE_COLOR, boxShadow: `0 0 10px ${LINE_COLOR}`,
              flexShrink: 0, marginTop: 5,
            }} />
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.3px' }}>
                {label}
              </div>
              <div style={{ marginTop: 5, fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
                {initialPeriod && <>{initialPeriod} · </>}MPI-Gesamtwert je Periode
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.07)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, color: 'rgba(255,255,255,0.45)',
              cursor: 'pointer', padding: '6px 12px', fontSize: 11, flexShrink: 0,
            }}
          >
            ✕ Schliessen
          </button>
        </div>

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {loading && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.18em' }}>
              LADE VERLAUFSDATEN …
            </div>
          )}
          {error && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 12, color: '#ef4444' }}>{error}</div>
          )}

          {!loading && !error && validCount < 2 && (
            <div style={{ padding: 40, textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
              Kein Verlauf verfügbar
            </div>
          )}

          {!loading && !error && validCount >= 2 && (
            <div style={{ width: '100%', height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 28, right: 24, bottom: 8, left: 0 }}>
                  <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    stroke={AXIS_COLOR}
                    tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: GRID_COLOR }}
                  />
                  <YAxis
                    domain={[0, 100]}
                    ticks={[0, 25, 50, 75, 100]}
                    allowDataOverflow
                    stroke={AXIS_COLOR}
                    tick={{ fill: AXIS_COLOR, fontSize: 12 }}
                    tickLine={false}
                    axisLine={{ stroke: GRID_COLOR }}
                    width={40}
                  />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: GRID_COLOR, strokeWidth: 1 }} />
                  <Line
                    type="monotone"
                    dataKey="mpi_total"
                    stroke={LINE_COLOR}
                    strokeWidth={2}
                    connectNulls={false}
                    dot={{ r: 3, fill: LINE_COLOR, strokeWidth: 0 }}
                    activeDot={{ r: 5 }}
                    isAnimationActive
                  >
                    <LabelList
                      dataKey="mpi_total"
                      position="top"
                      formatter={fmtComma}
                      style={{ fill: LABEL_COLOR, fontSize: 11, fontWeight: 600 }}
                    />
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
